// World renderer: draws terrain, cover, strategic points, structures, squads (via the
// procedural art module), effects, and order previews — all in world space through the
// camera. Model positions/facings are interpolated by `alpha` for smooth 60fps render
// over the 25tps sim.

import { angleDelta, dist, type Vec2 } from "../engine/vec";
import { coverPreviewAt } from "../sim/cover";
import type { CoverDef, Faction, Squad, StratPoint, Structure } from "../sim/types";
import type { World } from "../sim/world";
import { drawModel, KINDLED_PAL, LEGION_PAL, Palette, roundRectPath } from "./art";
import { Camera } from "./camera";

export interface ViewState {
  alpha: number;
  animTime: number;
  playerFaction: Faction;
  hoverWorld: Vec2 | null;
  selectionBox: { a: Vec2; b: Vec2 } | null; // screen coords
  showMovePreview: boolean;
  facingDrag: { from: Vec2; to: Vec2 } | null; // world coords
}

/**
 * A world renderer draws the battle scene (terrain, cover, squads, effects, and the
 * world-anchored order previews) for one frame. The Canvas-2D `Renderer` and the
 * PlayCanvas `PcRenderer` both implement it, so `BattleScene` is renderer-agnostic.
 * The screen-space HUD chrome is drawn separately by `Hud`.
 */
export interface IWorldRenderer {
  draw(world: World, view: ViewState): void;
  resize?(): void;
  destroy?(): void;
}

const OWNER_COLOR: Record<string, string> = { legion: "#c33", kindled: "#e0771b", neutral: "#8a8577" };

export class Renderer implements IWorldRenderer {
  ctx: CanvasRenderingContext2D;
  cam: Camera;

  constructor(ctx: CanvasRenderingContext2D, cam: Camera) {
    this.ctx = ctx;
    this.cam = cam;
  }

  pal(f: Faction): Palette {
    return f === "legion" ? LEGION_PAL : KINDLED_PAL;
  }

  draw(world: World, view: ViewState): void {
    const ctx = this.ctx;
    this.cam.applyTransform(ctx);
    this.drawGround(world);
    this.drawCover(world);
    this.drawPoints(world, view);
    this.drawStructures(world);
    if (view.showMovePreview && view.hoverWorld) this.drawMovePreview(world, view);
    this.drawSquads(world, view);
    this.drawEffects(world);
    if (view.facingDrag) this.drawFacingDrag(view.facingDrag);
    this.cam.resetTransform(ctx);
    if (view.selectionBox) this.drawSelectionBox(view.selectionBox);
  }

  private drawGround(world: World): void {
    const ctx = this.ctx;
    const g = ctx.createLinearGradient(0, 0, 0, world.map.height);
    g.addColorStop(0, "#211d19");
    g.addColorStop(1, "#171310");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, world.map.width, world.map.height);
    // faint ash mottling
    ctx.fillStyle = "rgba(60,52,44,0.35)";
    for (let i = 0; i < 60; i++) {
      const x = (i * 197) % world.map.width;
      const y = (i * 311) % world.map.height;
      ctx.beginPath();
      ctx.arc(x, y, 26 + ((i * 13) % 22), 0, Math.PI * 2);
      ctx.fill();
    }
    // border
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 6;
    ctx.strokeRect(0, 0, world.map.width, world.map.height);
  }

  private drawCover(world: World): void {
    const ctx = this.ctx;
    // roads first (negative cover — under everything)
    for (const c of world.cover) if (c.kind === "road") {
      ctx.fillStyle = "rgba(40,36,32,0.9)";
      roundRectPath(ctx, c.x!, c.y!, c.w!, c.h!, 6);
      ctx.fill();
      ctx.strokeStyle = "rgba(90,80,66,0.35)";
      ctx.setLineDash([14, 16]);
      ctx.lineWidth = 3;
      ctx.beginPath();
      if (c.w! > c.h!) {
        ctx.moveTo(c.x! + 8, c.y! + c.h! / 2);
        ctx.lineTo(c.x! + c.w! - 8, c.y! + c.h! / 2);
      } else {
        ctx.moveTo(c.x! + c.w! / 2, c.y! + 8);
        ctx.lineTo(c.x! + c.w! / 2, c.y! + c.h! - 8);
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // ruins (area cover)
    for (const c of world.cover) if (c.kind === "ruin") {
      ctx.fillStyle = "rgba(52,48,42,0.55)";
      ctx.beginPath();
      ctx.arc(c.x!, c.y!, c.radius!, 0, Math.PI * 2);
      ctx.fill();
      // broken wall chunks
      ctx.fillStyle = "#3c372f";
      ctx.strokeStyle = "#26221c";
      ctx.lineWidth = 2;
      const n = 7;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + (c.x! % 6);
        const rr = c.radius! * 0.78;
        const x = c.x! + Math.cos(a) * rr;
        const y = c.y! + Math.sin(a) * rr;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(a);
        roundRectPath(ctx, -10, -7, 20, 14, 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    }
    // craters
    for (const c of world.cover) if (c.kind === "crater") {
      const cx = (c.a!.x + c.b!.x) / 2;
      const cy = (c.a!.y + c.b!.y) / 2;
      const r = Math.max(20, dist(c.a!, c.b!));
      ctx.fillStyle = "rgba(20,17,14,0.8)";
      ctx.beginPath();
      ctx.ellipse(cx, cy, r, r * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#4a4238";
      ctx.lineWidth = 4;
      ctx.stroke();
    }
    // walls & sandbags (directional)
    for (const c of world.cover) if (c.kind === "wall" || c.kind === "sandbag") {
      const isWall = c.kind === "wall";
      ctx.strokeStyle = isWall ? "#5a5348" : "#6b5d43";
      ctx.lineWidth = isWall ? 12 : 16;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(c.a!.x, c.a!.y);
      ctx.lineTo(c.b!.x, c.b!.y);
      ctx.stroke();
      ctx.strokeStyle = isWall ? "#736a5b" : "#8a795a";
      ctx.lineWidth = isWall ? 5 : 7;
      ctx.stroke();
      if (!isWall) {
        // sandbag bumps
        ctx.fillStyle = "#7a6a4c";
        const len = dist(c.a!, c.b!);
        const steps = Math.max(2, Math.round(len / 22));
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          ctx.beginPath();
          ctx.arc(c.a!.x + (c.b!.x - c.a!.x) * t, c.a!.y + (c.b!.y - c.a!.y) * t, 7, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    // buildings
    for (const c of world.cover) if (c.kind === "building") {
      ctx.fillStyle = c.garrison ? "rgba(48,42,36,0.7)" : "#413a31";
      roundRectPath(ctx, c.x!, c.y!, c.w!, c.h!, 4);
      ctx.fill();
      ctx.strokeStyle = "#201c17";
      ctx.lineWidth = 4;
      ctx.stroke();
      if (c.garrison) {
        ctx.strokeStyle = "rgba(140,130,110,0.5)";
        ctx.setLineDash([8, 8]);
        ctx.lineWidth = 2;
        ctx.strokeRect(c.x! + 6, c.y! + 6, c.w! - 12, c.h! - 12);
        ctx.setLineDash([]);
      } else {
        // roof lines
        ctx.strokeStyle = "#2c2721";
        ctx.lineWidth = 2;
        for (let x = c.x! + 12; x < c.x! + c.w!; x += 16) {
          ctx.beginPath();
          ctx.moveTo(x, c.y!);
          ctx.lineTo(x, c.y! + c.h!);
          ctx.stroke();
        }
      }
    }
    void (0 as unknown as CoverDef);
  }

  private drawPoints(world: World, view: ViewState): void {
    const ctx = this.ctx;
    for (const p of world.points) {
      const col = OWNER_COLOR[p.owner];
      // capture radius ring
      ctx.strokeStyle = p.contested ? "#ffd24a" : `${col}`;
      ctx.globalAlpha = 0.35;
      ctx.setLineDash([10, 10]);
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(p.pos.x, p.pos.y, p.captureRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // base disc
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.beginPath();
      ctx.arc(p.pos.x, p.pos.y, 26, 0, Math.PI * 2);
      ctx.fill();

      // capture progress arc
      if (p.captureProgress > 0 && p.captureProgress < 1) {
        ctx.strokeStyle = OWNER_COLOR[p.capturing];
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.arc(p.pos.x, p.pos.y, 26, -Math.PI / 2, -Math.PI / 2 + p.captureProgress * Math.PI * 2);
        ctx.stroke();
      }

      // flag pole
      ctx.strokeStyle = "#2a251f";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(p.pos.x, p.pos.y + 6);
      ctx.lineTo(p.pos.x, p.pos.y - 34);
      ctx.stroke();
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.moveTo(p.pos.x, p.pos.y - 34);
      ctx.lineTo(p.pos.x + 26, p.pos.y - 28);
      ctx.lineTo(p.pos.x, p.pos.y - 18);
      ctx.closePath();
      ctx.fill();
      if (p.isPower) {
        ctx.fillStyle = "#7fd0ff";
        ctx.beginPath();
        ctx.arc(p.pos.x, p.pos.y, 7, 0, Math.PI * 2);
        ctx.fill();
      }
      if (p.hasOutpost) {
        ctx.strokeStyle = col;
        ctx.lineWidth = 3;
        ctx.strokeRect(p.pos.x - 30, p.pos.y - 8, 60, 20);
      }

      // label
      ctx.fillStyle = "rgba(230,224,212,0.85)";
      ctx.font = "600 13px Trebuchet MS, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`${p.name}${p.isPower ? "  ⚡" : ""}`, p.pos.x, p.pos.y + 46);
      ctx.textAlign = "left";
    }
    void view;
  }

  private drawStructures(world: World): void {
    const ctx = this.ctx;
    for (const s of world.structures) {
      const pal = this.pal(s.faction);
      ctx.save();
      ctx.translate(s.pos.x, s.pos.y);
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.beginPath();
      ctx.ellipse(0, s.radius * 0.5, s.radius * 1.2, s.radius * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
      this.drawStructureBody(s, pal);
      ctx.restore();
      // health bar if damaged
      if (s.hp < s.maxHp) this.bar(s.pos.x, s.pos.y - s.radius - 12, s.radius * 2, 5, s.hp / s.maxHp, s.faction === "legion" ? "#6fcf6f" : "#e0771b");
      if (s.buildProgress < 1) this.bar(s.pos.x, s.pos.y - s.radius - 12, s.radius * 2, 5, s.buildProgress, "#7fd0ff");
    }
  }

  private drawStructureBody(s: Structure, pal: Palette): void {
    const ctx = this.ctx;
    const r = s.radius;
    ctx.strokeStyle = "#14110d";
    ctx.lineWidth = 3;
    switch (s.kind) {
      case "hq":
        ctx.fillStyle = pal.body;
        roundRectPath(ctx, -r, -r, r * 2, r * 2, 8);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = pal.trim;
        roundRectPath(ctx, -r * 0.6, -r * 0.6, r * 1.2, r * 1.2, 6);
        ctx.fill();
        // banner
        ctx.fillStyle = pal.banner;
        roundRectPath(ctx, -r * 0.12, -r * 1.7, r * 0.24, r * 1.1, 3);
        ctx.fill();
        ctx.fillStyle = "#e8e0d4";
        ctx.font = "700 12px Trebuchet MS";
        ctx.textAlign = "center";
        ctx.fillText("HQ" + (s.tier > 1 ? " II" : ""), 0, 4);
        ctx.textAlign = "left";
        break;
      case "barracks":
        ctx.fillStyle = pal.body;
        roundRectPath(ctx, -r, -r * 0.8, r * 2, r * 1.6, 6);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = pal.trim;
        roundRectPath(ctx, -r, -r * 0.8, r * 2, r * 0.4, 4);
        ctx.fill();
        break;
      case "generator":
        ctx.fillStyle = "#3c4650";
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.strokeStyle = "#7fd0ff";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-r * 0.4, r * 0.4);
        ctx.lineTo(0, -r * 0.3);
        ctx.lineTo(r * 0.2, r * 0.1);
        ctx.lineTo(r * 0.5, -r * 0.4);
        ctx.stroke();
        break;
      case "turret":
        ctx.fillStyle = pal.steel;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.strokeStyle = "#14110d";
        ctx.lineWidth = r * 0.35;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(r * 1.6, 0);
        ctx.stroke();
        break;
      case "outpost":
        ctx.fillStyle = pal.body;
        for (let i = 0; i < 4; i++) {
          const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
          ctx.fillRect(Math.cos(a) * r - 4, Math.sin(a) * r - 4, 8, 8);
        }
        ctx.strokeStyle = pal.trim;
        ctx.lineWidth = 3;
        ctx.strokeRect(-r * 0.7, -r * 0.7, r * 1.4, r * 1.4);
        break;
    }
  }

  private interp(prev: Vec2, cur: Vec2, a: number): Vec2 {
    return { x: prev.x + (cur.x - prev.x) * a, y: prev.y + (cur.y - prev.y) * a };
  }

  private drawSquads(world: World, view: ViewState): void {
    const ctx = this.ctx;
    // selection rings first (under models)
    for (const sq of world.squads) {
      if (sq.dead || !sq.selected) continue;
      const ring = sq.faction === view.playerFaction ? "rgba(120,220,120,0.9)" : "rgba(220,90,90,0.9)";
      for (const m of sq.models) {
        if (!m.alive) continue;
        const p = this.interp(m.prevPos, m.pos, view.alpha);
        ctx.strokeStyle = ring;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(p.x, p.y + 6, 12, 6, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    // models
    for (const sq of world.squads) {
      if (sq.dead) continue;
      const pal = this.pal(sq.faction);
      for (const m of sq.models) {
        if (!m.alive) continue;
        const p = this.interp(m.prevPos, m.pos, view.alpha);
        const facing = m.prevFacing + angleDelta(m.prevFacing, m.facing) * view.alpha;
        drawModel(ctx, sq, m, p, facing, pal, view.animTime);
      }
    }
    // squad status bars + posture
    for (const sq of world.squads) {
      if (sq.dead) continue;
      this.drawSquadStatus(world, sq, view);
    }
  }

  private drawSquadStatus(world: World, sq: Squad, view: ViewState): void {
    const ctx = this.ctx;
    // anchor above the topmost alive model
    let top: Vec2 | null = null;
    let hpSum = 0;
    let hpMax = 0;
    for (const m of sq.models) {
      if (!m.alive) continue;
      const p = this.interp(m.prevPos, m.pos, view.alpha);
      if (!top || p.y < top.y) top = p;
      hpSum += m.hp;
      hpMax += m.maxHp;
    }
    if (!top) return;
    const isVehicle = sq.def.role === "vehicle";
    const w = isVehicle ? 44 : 34;
    const bx = top.x - w / 2;
    const by = top.y - (isVehicle ? 34 : 22);
    const own = sq.faction === view.playerFaction;
    // health
    this.bar(top.x, by, w, 4, hpMax ? hpSum / hpMax : 0, own ? "#6fcf6f" : "#d05555");
    // morale (under health)
    const mc = sq.broken ? "#d0483c" : "#e0b83c";
    this.bar(top.x, by + 6, w, 3, sq.morale / sq.maxMorale, mc);
    // suppression tick overlay
    if (sq.suppression > 0.05) {
      ctx.fillStyle = "rgba(180,180,190,0.7)";
      ctx.fillRect(bx, by + 11, w * Math.min(1, sq.suppression), 2);
    }
    // model pips
    const alive = sq.models.filter((m) => m.alive).length;
    ctx.fillStyle = "rgba(230,224,212,0.85)";
    for (let i = 0; i < sq.def.modelCount; i++) {
      ctx.fillStyle = i < alive ? "rgba(230,224,212,0.9)" : "rgba(120,110,100,0.5)";
      ctx.fillRect(bx + i * 5, by - 5, 3.5, 3);
    }
    // status label
    let tag = "";
    if (sq.frenzy) tag = "FRENZY";
    else if (sq.broken) tag = "BROKEN";
    else if (sq.suppression >= 0.72) tag = "PINNED";
    else if (sq.discipline > 0.5) tag = "DUG IN";
    if (tag) {
      ctx.font = "700 9px Trebuchet MS";
      ctx.textAlign = "center";
      ctx.fillStyle = sq.frenzy ? "#ff8a2c" : sq.broken ? "#ff6a5a" : sq.suppression >= 0.72 ? "#cfcfe0" : "#ffe089";
      ctx.fillText(tag, top.x, by - 8);
      ctx.textAlign = "left";
    }
    void world;
  }

  private bar(cx: number, y: number, w: number, h: number, frac: number, color: string): void {
    const ctx = this.ctx;
    const x = cx - w / 2;
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(x - 1, y - 1, w + 2, h + 2);
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w * Math.max(0, Math.min(1, frac)), h);
  }

  private drawMovePreview(world: World, view: ViewState): void {
    const ctx = this.ctx;
    const p = view.hoverWorld!;
    const pips = coverPreviewAt(world, p, view.playerFaction);
    ctx.strokeStyle = "rgba(120,220,120,0.7)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 16, 0, Math.PI * 2);
    ctx.stroke();
    // cover shield pips
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = i < pips ? "#8fd8ff" : "rgba(120,120,120,0.4)";
      const px = p.x - 14 + i * 14;
      ctx.beginPath();
      ctx.moveTo(px, p.y - 30);
      ctx.lineTo(px + 5, p.y - 27);
      ctx.lineTo(px + 5, p.y - 20);
      ctx.lineTo(px, p.y - 17);
      ctx.lineTo(px - 5, p.y - 20);
      ctx.lineTo(px - 5, p.y - 27);
      ctx.closePath();
      ctx.fill();
    }
  }

  private drawFacingDrag(fd: { from: Vec2; to: Vec2 }): void {
    const ctx = this.ctx;
    ctx.strokeStyle = "rgba(120,220,120,0.8)";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.moveTo(fd.from.x, fd.from.y);
    ctx.lineTo(fd.to.x, fd.to.y);
    ctx.stroke();
    ctx.setLineDash([]);
    // arrowhead
    const a = Math.atan2(fd.to.y - fd.from.y, fd.to.x - fd.from.x);
    ctx.fillStyle = "rgba(120,220,120,0.9)";
    ctx.save();
    ctx.translate(fd.to.x, fd.to.y);
    ctx.rotate(a);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-12, -6);
    ctx.lineTo(-12, 6);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private drawEffects(world: World): void {
    const ctx = this.ctx;
    for (const e of world.effects) {
      const k = e.t / e.ttl;
      switch (e.kind) {
        case "tracer": {
          if (!e.b) break;
          ctx.strokeStyle = e.color ?? "#ffcf70";
          ctx.globalAlpha = Math.max(0, 1 - k);
          ctx.lineWidth = e.size ?? 1.4;
          ctx.beginPath();
          ctx.moveTo(e.a.x, e.a.y);
          ctx.lineTo(e.b.x, e.b.y);
          ctx.stroke();
          ctx.globalAlpha = 1;
          break;
        }
        case "muzzle": {
          ctx.fillStyle = `rgba(255,235,160,${1 - k})`;
          ctx.beginPath();
          ctx.arc(e.a.x, e.a.y, 4 * (1 - k) + 2, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case "blood": {
          ctx.fillStyle = e.faction === "kindled" ? `rgba(255,140,40,${0.7 * (1 - k)})` : `rgba(150,20,20,${0.7 * (1 - k)})`;
          ctx.beginPath();
          ctx.arc(e.a.x, e.a.y, (e.size ?? 2) * (1 + k * 2), 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case "dust": {
          ctx.fillStyle = `rgba(120,110,96,${0.5 * (1 - k)})`;
          ctx.beginPath();
          ctx.arc(e.a.x, e.a.y, (e.size ?? 6) * (1 + k * 2), 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case "fire": {
          const r = (e.size ?? 40) * (0.5 + k * 0.7);
          const grd = ctx.createRadialGradient(e.a.x, e.a.y, 0, e.a.x, e.a.y, r);
          grd.addColorStop(0, `rgba(255,220,120,${0.7 * (1 - k)})`);
          grd.addColorStop(0.5, `rgba(255,110,30,${0.5 * (1 - k)})`);
          grd.addColorStop(1, "rgba(120,20,0,0)");
          ctx.fillStyle = grd;
          ctx.beginPath();
          ctx.arc(e.a.x, e.a.y, r, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case "explosion": {
          ctx.strokeStyle = `rgba(255,200,120,${1 - k})`;
          ctx.lineWidth = 3 * (1 - k) + 1;
          ctx.beginPath();
          ctx.arc(e.a.x, e.a.y, (e.size ?? 20) * (0.3 + k), 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case "flash": {
          ctx.strokeStyle = e.faction === "kindled" ? `rgba(255,140,50,${0.7 * (1 - k)})` : `rgba(255,220,150,${0.7 * (1 - k)})`;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(e.a.x, e.a.y, (e.size ?? 12) * (0.4 + k), 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case "text": {
          ctx.fillStyle = e.faction === "kindled" ? `rgba(255,150,60,${1 - k})` : `rgba(255,225,150,${1 - k})`;
          ctx.font = "700 15px Trebuchet MS";
          ctx.textAlign = "center";
          ctx.fillText(e.text ?? "", e.a.x, e.a.y - 26 - k * 20);
          ctx.textAlign = "left";
          break;
        }
      }
    }
  }

  private drawSelectionBox(box: { a: Vec2; b: Vec2 }): void {
    const ctx = this.ctx;
    const x = Math.min(box.a.x, box.b.x);
    const y = Math.min(box.a.y, box.b.y);
    const w = Math.abs(box.a.x - box.b.x);
    const h = Math.abs(box.a.y - box.b.y);
    ctx.fillStyle = "rgba(120,220,120,0.1)";
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = "rgba(120,220,120,0.8)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x, y, w, h);
  }

  static pointColor(p: StratPoint): string {
    return OWNER_COLOR[p.owner];
  }
}
